/**
 * Home shortcut tiles — 4 Pillar entry points.
 *
 *   1. My Goals         → /goals
 *   2. Family & Friends → /assign
 *   3. Explore UAE      → /explore
 *   4. Passports        → /passports
 *
 * Mirrors the bottom/top nav so users build one clear mental model.
 * <ExploreMapHero/> is kept as a compat export used elsewhere.
 */
import { useState } from "react";
import { Target, Users, Map, Stamp, MapPin, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { AuthModal } from "@/components/auth/AuthModal";

type Pillar = {
  key: string;
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string;
  requiresAuth?: boolean;
};

const PILLARS: Pillar[] = [
  { key: "goals",     to: "/goals",     label: "My Goals",  icon: Target, gradient: "from-orange-500 to-rose-500", requiresAuth: true },
  { key: "family",    to: "/assign",    label: "Family",    icon: Users,  gradient: "from-pink-500 to-fuchsia-500", requiresAuth: true },
  { key: "explore",   to: "/explore",   label: "Explore",   icon: Map,    gradient: "from-sky-500 to-emerald-500" },
  { key: "passports", to: "/passports", label: "Passports", icon: Stamp,  gradient: "from-amber-500 to-orange-500" },
];

export function HomeShortcutsRow() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <>
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => {
                if (p.requiresAuth && !user) return setAuthOpen(true);
                navigate(p.to);
              }}
              style={{ touchAction: "manipulation" }}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 rounded-2xl border p-2.5 sm:p-3",
                "shadow-sm hover:shadow-md transition-all active:scale-[0.97] min-h-[78px]",
                "bg-card hover:bg-accent border-border"
              )}
              aria-label={p.label}
            >
              <span
                className={cn(
                  "h-9 w-9 rounded-xl flex items-center justify-center shadow-sm bg-gradient-to-br",
                  p.gradient
                )}
                aria-hidden
              >
                <Icon className="h-5 w-5 text-primary-foreground" />
              </span>
              <span className="text-[11px] sm:text-xs font-semibold text-foreground text-center leading-tight">
                {p.label}
              </span>
            </button>
          );
        })}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}

const EMIRATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"];

/** Full-bleed editorial Explore spread — dark viewfinder band with emirate entry chips. */
export function ExploreMapHero() {
  const navigate = useNavigate();
  return (
    <section aria-label="Explore the UAE" className="gd-hero-deck relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(hsl(0_0%_100%/0.12)_1px,transparent_1px)] [background-size:22px_22px]" />
      <div aria-hidden className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-primary/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      <div className="relative grid gap-6 p-6 sm:p-10 lg:grid-cols-12 lg:items-end lg:gap-10 lg:px-14 lg:py-14">
        <div className="lg:col-span-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary-glow">Chapter 02 · Explore UAE · 7 emirates</p>
          <h2 className="gd-display mt-3 text-3xl sm:text-5xl xl:text-6xl">
            Your city, <span className="text-primary-glow">curated.</span>
          </h2>
          <p className="mt-3 max-w-xl text-sm sm:text-base leading-relaxed opacity-80">
            A live map of places worth going to — save them, plan the day, live it and collect the stamp.
          </p>
          <button
            type="button"
            onClick={() => navigate("/explore")}
            style={{ touchAction: "manipulation" }}
            className="group mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform active:scale-[0.98]"
          >
            <MapPin className="h-4 w-4" /> Open the map
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
        <div className="lg:col-span-5">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] opacity-60">Jump to an emirate</p>
          <div className="flex flex-wrap gap-2">
            {EMIRATES.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => navigate(`/explore?emirate=${encodeURIComponent(e)}`)}
                style={{ touchAction: "manipulation" }}
                className="rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-xs font-semibold backdrop-blur transition hover:bg-white/20 active:scale-[0.97]"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomeShortcutsRow;
